import { useState } from "react";
import { Card, Input, Table, Tag, Button, Modal, Form, Select, message, Space, Popconfirm } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, AdminUser } from "../api";

export default function Users() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["users", q, page],
    queryFn: async () =>
      (await api.get<{ items: AdminUser[] }>("/users", { params: { q, limit: pageSize, offset: (page - 1) * pageSize } })).data,
  });

  const ban = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: number; reason?: string }) =>
      api.post(`/users/${id}/status`, { status, reason }),
    onSuccess: () => { message.success("已更新"); qc.invalidateQueries({ queryKey: ["users"] }); },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => { message.success("已删除"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "删除失败"),
  });

  const [target, setTarget] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();

  return (
    <Card
      style={{ background: "#10131A", border: "1px solid #1A1D26" }}
      title={<span style={{ color: "#F5F5F7" }}>用户管理</span>}
      extra={<Input.Search placeholder="username / 钱包地址" allowClear onSearch={(v) => { setQ(v); setPage(1); }} style={{ width: 280 }} />}
    >
      <Table<AdminUser>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items}
        pagination={{ current: page, pageSize, total: data?.items?.length === pageSize ? page * pageSize + 1 : (page - 1) * pageSize + (data?.items?.length ?? 0), onChange: setPage, showSizeChanger: false }}
        columns={[
          { title: "用户名", dataIndex: "username", render: (v) => v ?? <span style={{ color: "#6B7280" }}>未设置</span> },
          { title: "钱包地址", dataIndex: "wallet_addr", render: (v) => <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>{v.slice(0, 6)}…{v.slice(-4)}</span> },
          { title: "状态", dataIndex: "status", width: 100, render: (s: number) => s === 0 ? <Tag color="green">正常</Tag> : s === 1 ? <Tag color="orange">禁言</Tag> : <Tag color="red">封禁</Tag> },
          { title: "注册时间", dataIndex: "created_at", width: 180, render: (v) => new Date(v).toLocaleString() },
          {
            title: "操作", width: 180,
            render: (_, r) => (
              <Space>
                <Button size="small" onClick={() => { setTarget(r); form.setFieldsValue({ status: r.status }); }}>调整状态</Button>
                <Popconfirm title="确认删除此用户?" onConfirm={() => del.mutate(r.id)}>
                  <Button size="small" danger>删除</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal title={`处理 ${target?.username ?? target?.wallet_addr}`} open={!!target} onCancel={() => setTarget(null)}
        onOk={async () => { const v = await form.validateFields(); ban.mutate({ id: target!.id, ...v }); setTarget(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select options={[{ value: 0, label: "正常" }, { value: 1, label: "禁言" }, { value: 2, label: "封禁" }]} />
          </Form.Item>
          <Form.Item name="reason" label="原因(写入审计)"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
